import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Typography, message, Alert, Button } from 'antd';
import { registerEmail as registerEmailAuth } from '../../auth';
import styles from './Auth.module.css';
import RegisterEmailForm from './RegisterEmail';
import { isTestPublic } from '../../utils/env';

const { Title } = Typography;

const registrationSuspended = isTestPublic;

const HEADLINE_FULL = 'SEOライティングの決定版。\nプロ品質をAIと共に。';

const Register = () => {
  const navigate = useNavigate();
  const [utmParams, setUtmParams] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const location = useLocation();
  const [typedLen, setTypedLen] = useState(0);

  /* ── タイプライターアニメーション ── */
  useEffect(() => {
    if (typedLen >= HEADLINE_FULL.length) return;
    const id = setTimeout(() => setTypedLen(prev => prev + 1), 80);
    return () => clearTimeout(id);
  }, [typedLen]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref'].forEach(
      param => {
        const value = searchParams.get(param);
        if (value) {
          params[param] = value;
        }
      },
    );

    // Meta 広告トラッキング: fbclid (URLパラメータ)
    const fbclid = searchParams.get('fbclid');
    if (fbclid) {
      params.fbclid = fbclid;
    }

    // Meta 広告トラッキング: _fbc, _fbp (Cookie)
    const getCookie = (name) => {
      const match = document.cookie.match('(^|;)\\s*' + name + '=([^;]*)');
      return match ? decodeURIComponent(match[2]) : '';
    };
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');
    if (fbc) params.meta_fbc = fbc;
    if (fbp) params.meta_fbp = fbp;

    const storedParams = JSON.parse(localStorage.getItem('utmParams') || '{}');
    const combinedParams = { ...storedParams, ...params };

    if (Object.keys(combinedParams).length > 0) {
      setUtmParams(combinedParams);
      localStorage.setItem('utmParams', JSON.stringify(combinedParams));
    }
  }, [location]);

  const handleRegisterEmail = async values => {
    try {
      const registerData = {
        email: values.email,
        utm_params: utmParams,
      };
      await registerEmailAuth(registerData);
      message.success('認証URLを送信しました。メールをご確認ください。');
      setEmailSent(true);
      return true;
    } catch (error) {
      if (error.response && error.response.status === 400) {
        message.error(error.response.data.detail);
      } else {
        message.error('認証URLの送信に失敗しました');
      }
      return false;
    }
  };

  /* タイプライター表示テキストを行ごとに分割 */
  const visibleText = HEADLINE_FULL.slice(0, typedLen);
  const headlineLines = visibleText.split('\n');
  const isTyping = typedLen < HEADLINE_FULL.length;

  return (
    <div className={styles.registerPage}>
      {/* 全画面グラデーション背景 + 揺らぎ（CSS ::before） */}
      <div className={styles.registerInner}>
        {/* 左: コピーテキスト */}
        <div className={styles.registerHero}>
          <h1 className={styles.registerHeadline}>
            {headlineLines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
            <span className={`${styles.typewriterCursor} ${isTyping ? styles.typing : ''}`} />
          </h1>
          <ul className={styles.registerBenefits}>
            <li>リサーチ〜執筆まで丸っと無料でお試し可能</li>
            <li>SEOに詳しい人ほど驚くクオリティ</li>
            <li>1文字あたり約0.1円で、実用レベルの原稿を</li>
          </ul>
        </div>

        {/* 右: 登録カード */}
        <div className={styles.registerCard}>
          <div className="logo outside" style={{ marginBottom: 20 }}>
            <span>Ignite creativity with..</span>
            magicss
          </div>
          <Title
            level={3}
            style={{
              textAlign: 'center',
              marginBottom: 6,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            無料アカウントを作成
          </Title>
          <div
            style={{
              textAlign: 'center',
              color: '#999',
              fontSize: 13,
              marginBottom: 28,
            }}
          >
            ＼最短2分でスタート／
          </div>

          {registrationSuspended ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Alert
                type="warning"
                showIcon
                message="現在、新規登録を停止しております"
                description={
                  <div style={{ marginTop: 8 }}>
                    近日中に再開を予定しておりますが、その際は公式Xアカウントでご案内させていただきます。
                    <div style={{ marginTop: 12 }}>
                      <a
                        href="https://twitter.com/magicss_ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 500 }}
                      >
                        @magicss_ai をフォロー
                      </a>
                    </div>
                  </div>
                }
                style={{ marginBottom: 24 }}
              />
              <Button type="primary" onClick={() => navigate('/login')}>
                ログインページへ
              </Button>
            </div>
          ) : (
            <>
              <RegisterEmailForm
                onFinish={handleRegisterEmail}
                utmParams={utmParams}
              />

              {!emailSent && (
                <div
                  className={styles.registerFooterLinks}
                  style={{ marginTop: 24, textAlign: 'center', fontSize: 13 }}
                >
                  <Link to="/login" style={{ color: '#888' }}>
                    既にアカウントをお持ちの方
                  </Link>
                  <span className={styles.registerPipe} style={{ margin: '0 12px', color: '#e0e0e0' }}>|</span>
                  <Link
                    to="../privacy-policy"
                    target="_blank"
                    style={{ color: '#888' }}
                  >
                    プライバシーポリシー
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;
